const HAPTIC_EVENT = 'web_app_trigger_haptic_feedback';
const HAPTIC_MIN_VERSION = '6.1';

function getWindow() {
  return typeof window === 'undefined' ? null : window;
}

function getCurrentWebApp(fallbackWebApp) {
  return getWindow()?.Telegram?.WebApp ?? fallbackWebApp ?? null;
}

function getImpactPayload(style) {
  return {
    type: 'impact',
    impact_style: style,
  };
}

function getNotificationPayload(type) {
  return {
    type: 'notification',
    notification_type: type,
  };
}

function isHapticVersionSupported(webApp) {
  if (!webApp) return false;

  if (typeof webApp.isVersionAtLeast === 'function') {
    return webApp.isVersionAtLeast(HAPTIC_MIN_VERSION);
  }

  return true;
}

function postBridgeEvent(eventType, eventData) {
  const win = getWindow();
  if (!win) {
    return null;
  }

  if (typeof win.TelegramWebviewProxy?.postEvent === 'function') {
    win.TelegramWebviewProxy.postEvent(eventType, JSON.stringify(eventData));
    return 'TelegramWebviewProxy.postEvent';
  }

  if (typeof win.external?.notify === 'function') {
    win.external.notify(JSON.stringify({ eventType, eventData }));
    return 'window.external.notify';
  }

  if (win.parent && win.parent !== win && typeof win.parent.postMessage === 'function') {
    win.parent.postMessage(JSON.stringify({ eventType, eventData }), '*');
    return 'window.parent.postMessage';
  }

  return null;
}

function callWebAppHaptic(webApp, payload) {
  const haptic = webApp?.HapticFeedback;
  if (!haptic) return null;

  if (payload.type === 'impact' && typeof haptic.impactOccurred === 'function') {
    haptic.impactOccurred(payload.impact_style);
    return 'WebApp.HapticFeedback.impactOccurred';
  }

  if (payload.type === 'notification' && typeof haptic.notificationOccurred === 'function') {
    haptic.notificationOccurred(payload.notification_type);
    return 'WebApp.HapticFeedback.notificationOccurred';
  }

  if (typeof haptic.selectionChanged === 'function') {
    haptic.selectionChanged();
    return 'WebApp.HapticFeedback.selectionChanged';
  }

  return null;
}

function writeHapticDebug(data) {
  const win = getWindow();
  if (!win) return;

  win.__obankHapticDebug = {
    at: new Date().toISOString(),
    ...data,
  };
}

function triggerHaptic(payload, debugData = {}, fallbackWebApp = null) {
  const win = getWindow();
  const webApp = getCurrentWebApp(fallbackWebApp);
  const debugBase = {
    ...debugData,
    hasWindowTelegram: Boolean(win?.Telegram),
    hasWebApp: Boolean(webApp),
    version: webApp?.version ?? null,
    platform: webApp?.platform ?? null,
    isVersionAtLeast61: webApp?.isVersionAtLeast?.(HAPTIC_MIN_VERSION) ?? null,
    hasHapticFeedback: Boolean(webApp?.HapticFeedback),
    hasTelegramWebviewProxy: typeof win?.TelegramWebviewProxy?.postEvent === 'function',
    hasExternalNotify: typeof win?.external?.notify === 'function',
  };

  try {
    const bridgeTransport = postBridgeEvent(HAPTIC_EVENT, payload);
    if (bridgeTransport) {
      writeHapticDebug({
        ...debugBase,
        ok: true,
        transport: bridgeTransport,
        payload,
      });
      return true;
    }

    if (!isHapticVersionSupported(webApp)) {
      writeHapticDebug({
        ...debugBase,
        ok: false,
        reason: `WebApp version below ${HAPTIC_MIN_VERSION}`,
        payload,
      });
      return false;
    }

    const webAppMethod = callWebAppHaptic(webApp, payload);
    writeHapticDebug({
      ...debugBase,
      ok: Boolean(webAppMethod),
      transport: webAppMethod,
      payload,
    });
    return Boolean(webAppMethod);
  } catch (error) {
    writeHapticDebug({
      ...debugBase,
      ok: false,
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
}

export function triggerImpactHaptic(style = 'light', fallbackWebApp = null) {
  return triggerHaptic(getImpactPayload(style), { style, hapticType: 'impact' }, fallbackWebApp);
}

export function triggerNotificationHaptic(type = 'error', fallbackWebApp = null) {
  return triggerHaptic(
    getNotificationPayload(type),
    { notificationType: type, hapticType: 'notification' },
    fallbackWebApp
  );
}
